import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Overlay } from "react-vant";
import UserTip from "../ui/UserTip";
import { useTranslation } from "next-i18next";
import Input from "@/components/login/ui/Input";
import { checkEmailRegister, login } from "@/pages/api/auth";
import ErrorMsg from "../ui/ErrorMsg";
import Cookies from "js-cookie";
import { eventBus, LOGIN_SUCCESS_EVENT } from "@/utils/eventBus";
import PasswordInput from "../ui/PasswordInput";
import { sendSlsEvent } from "@/hooks/useAnalytics";
import { useElapsedTime } from "@/utils/useElapsedTime";
import { toast } from "@/components/common/Toast";
import { useRouter } from "next/router";
interface EmailFormProps {
  onClose: () => void;
  goRegistry: () => void;
  goVerifyCode: () => void;
  onEmailChange: (email: string) => void;
  onVerifyCodeType: (type: string) => void;
  onFocusChange: (focus: boolean) => void;
  autoFocus?: boolean;
  userInputEmail: string;
  goEmailRegistred: () => void;
  handleEmailGoogleRegistredAndNoPasswordOrigin: (
    origin: "login" | "registry"
  ) => void;
}

interface FormData {
  email: string;
  password: string;
}

// 预编译正则表达式
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailForm = ({
  goVerifyCode,
  onEmailChange,
  onVerifyCodeType,
  onClose,
  goRegistry,
  onFocusChange,
  autoFocus = false,
  userInputEmail,
  goEmailRegistred,
  handleEmailGoogleRegistredAndNoPasswordOrigin,
}: EmailFormProps) => {
  const getElapsed = useElapsedTime();
  const pageId = "maillogin_web";
  const { t } = useTranslation();
  const router = useRouter();
  const code = router?.query?.code as string;

  const [formData, setFormData] = useState<FormData>({
    email: userInputEmail,
    password: "",
  });

  const [errorMsg, setErrorMsg] = useState({
    email: {
      message: "",
      show: false,
    },
    password: {
      message: "",
      show: false,
    },
  });
  const [disabledLogin, setDisabledLogin] = useState(true);
  const [showUnRegisterDialog, setShowUnRegisterDialog] = useState(false);
  const [showRegistedDialog, setShowRegistedDialog] = useState(false);

  // 使用useRef跟踪是否已经进行过输入
  const hasInteracted = useRef({
    email: false,
    password: false,
  });

  // 使用useMemo优化表单验证逻辑
  const formValidation = useMemo(() => {
    const result = {
      isValid: false,
      emailError: {
        message: "",
        show: false,
      },
      passwordError: {
        message: "",
        show: false,
      },
    };

    // 只有在用户已经与字段交互后才显示错误
    if (hasInteracted.current.email) {
      if (!formData.email) {
        result.emailError = { message: t("login.emailnotEmpty"), show: true };
      } else if (!emailRegex.test(formData.email)) {
        result.emailError = {
          message: t("login.emailnotvalid"),
          show: true,
        };
      }
    }

    if (hasInteracted.current.password) {
      if (!formData.password) {
        result.passwordError = {
          message: t("login.pswnotEmpty"),
          show: true,
        };
      }
    }

    // 验证表单是否有效（无论是否显示错误）
    result.isValid =
      !!formData.email &&
      emailRegex.test(formData.email) &&
      !!formData.password;

    return result;
  }, [formData, hasInteracted.current]);

  // 使用useEffect更新状态，减少重复渲染
  useEffect(() => {
    setErrorMsg({
      email: formValidation.emailError,
      password: formValidation.passwordError,
    });
    setDisabledLogin(!formValidation.isValid);
  }, [formValidation]);
  const handleLeft = () => {
    console.log("handleLeft---email-login----");
    sendSlsEvent({
      UM_Key_EventName: "UM_Event_PageLeave",
      UM_Key_PageName: pageId,
      UM_Key_UserType: "Guest",
      UM_Key_SourcePage: router.route,
      UM_Key_DistinctID: "",
      UM_Key_UserID: "",
      UM_Key_Duration: getElapsed(),
    });
  };
  useEffect(() => {
    sendSlsEvent({
      UM_Key_EventName: "UM_Event_PageView",
      UM_Key_PageName: pageId,
      UM_Key_UserType: "Guest",
      UM_Key_SourcePage: router.route,
      UM_Key_DistinctID: "",
      UM_Key_UserID: "",
    });
  }, []);
  useEffect(() => {
    return () => {
      handleLeft();
    };
  }, []);
  // 使用useCallback优化事件处理函数
  const handleEmailChange = useCallback(
    (v: string) => {
      hasInteracted.current.email = true;
      onEmailChange(v);
      setFormData((prev) => ({ ...prev, email: v }));
    },
    [onEmailChange]
  );

  const handlePasswordChange = useCallback((v: string) => {
    hasInteracted.current.password = true;
    setFormData((prev) => ({ ...prev, password: v }));
  }, []);

  const handleUnregisterDialogClose = useCallback(() => {
    setShowUnRegisterDialog(false);
  }, []);

  const handleGoRegistry = useCallback(() => {
    setShowUnRegisterDialog(false);
    goRegistry();
  }, [goRegistry]);

  // 登录
  const handleLogin = useCallback(async () => {
    // 点击登录按钮时，标记所有字段为已交互
    hasInteracted.current = {
      email: true,
      password: true,
    };

    if (disabledLogin) {
      return;
    }

    // 检查邮箱是否注册
    const res = await checkEmailRegister({
      email: formData.email,
      type: 0,
    });
    const { isRegister, hasPassword, isThirdLogin } = res || {};
    if (!isRegister) {
      setShowUnRegisterDialog(true);
      return;
    }
    // 已经注册
    if (isRegister) {
      if (!hasPassword) {
        // 设置密码， 或者直接第三方登录
        console.log("是否第三方登录", isThirdLogin);
        // 跳转到设置密码
        handleEmailGoogleRegistredAndNoPasswordOrigin("login");
        goEmailRegistred();
        return;
      }
    }

    //登录
    login({
      email: formData.email,
      password: formData.password,
    })
      .then((res) => {
        console.log("res-----", res);
        if (res?.data.userInfoBaseResp?.token) {
          console.log(
            "res?.data.userInfoBaseResp?.token",
            res?.data.userInfoBaseResp?.token
          );
          // 设置 cookie，有效期 7 天
          Cookies.set("token", res?.data.userInfoBaseResp?.token, {
            expires: 30,
            path: "/",
            sameSite: "lax",
          });
          Cookies.set("uid", res?.data.userInfoBaseResp?.id, {
            expires: 30,
            path: "/",
            sameSite: "lax",
          });
          // 登录成功
          localStorage.setItem("isLoggedIn", "true");
          // 触发登录成功事件
          eventBus.emit(LOGIN_SUCCESS_EVENT, {
            userId: res?.data.userInfoBaseResp?.id,
            userInfoBaseResp: res?.data.userInfoBaseResp,
          });
          onClose();
        }
      })
      .catch((err) => {
        console.log("err", err);
      });
  }, [formData, disabledLogin, onClose]);

  const errorBorder = "border border-[#ff0000] border-[2px]";

  const handleForgotPassword = useCallback(
    async (email: string) => {
      console.log("email", email);

      // 检查邮箱是否为空
      if (!email) {
        setErrorMsg((pre) => {
          return {
            ...pre,
            email: {
              message: t("login.emailnotEmpty"),
              show: true,
            },
          };
        });
        return;
      }
      if (formValidation.emailError.show) {
        return;
      }
      // 检查邮箱是否注册
      try {
        const { isRegister, hasPassword } = await checkEmailRegister({
          email: email,
          type: 0,
        });
        if (!isRegister) {
          // 未注册
          setShowUnRegisterDialog(true);
          return;
        }
        // 已经注册
        else if (isRegister) {
          if (!hasPassword) {
            // 设置密码， 或者直接第三方登录
            // console.log("是否第三方登录", isThirdLogin);
            // 跳转到设置密码
            handleEmailGoogleRegistredAndNoPasswordOrigin("login");
            goEmailRegistred();
            return;
          }
        }

        onVerifyCodeType("1");
        goVerifyCode();
      } catch (error) {
        console.log("error", error);
        toast.error(t("login.emailnotvalid"));
      }
    },
    [goVerifyCode, onVerifyCodeType, t]
  );

  return (
    <div className="flex flex-col w-[calc(100vw-40px)]">
      <div className="w-full mb-[18px] mt-[26px]">
        <Input
          value={formData.email}
          onChange={handleEmailChange}
          onFocusChange={onFocusChange}
          autoFocus={autoFocus}
          classNames={`bg-[#f2f2f2] rounded-[100px]  h-[56px] ps-[20px] pe-[20px] font-[#333]  font-[700] text-[16px] font-['Mulish'] ${
            errorMsg.email.show ? errorBorder : ""
          }`}
          placeholder={t("login.emailDesc")}
        />
        <ErrorMsg
          message={errorMsg.email.message}
          show={errorMsg.email.show}
        ></ErrorMsg>
      </div>

      <div className="w-full ">
        <PasswordInput
          value={formData.password}
          onChange={handlePasswordChange}
          onFocusChange={onFocusChange}
          placeholder={t("login.pswDesc")}
          classNames={`bg-[#f2f2f2]  text-[#333]  rounded-[100px] h-[56px] ps-[20px] pe-[20px] font-[700] text-[16px] font-['Mulish'] ${
            errorMsg.password.show ? errorBorder : ""
          }`}
        ></PasswordInput>
        <ErrorMsg
          message={errorMsg.password.message}
          show={errorMsg.password.show}
        ></ErrorMsg>
      </div>

      <div className="w-full flex justify-end mb-[18px] mt-[6px]">
        <span
          onClick={() => {
            sendSlsEvent({
              UM_Key_EventName: "UM_Event_ModularClick",
              UM_Key_ButtonName: "forgetpassword",
              UM_Key_PageName: pageId,
              UM_Key_UserType: "Guest",
              UM_Key_SourcePage: router.route,
              UM_Key_ResourceID: code,
            });
            handleForgotPassword(formData.email);
          }}
          className="w-fit flex justify-end text-[#1677ff] text-[14px] font-['Mulish'] font-[700] "
        >
          {t("login.forgot")}
        </span>
      </div>

      <div
        onClick={() => {
          sendSlsEvent({
            UM_Key_EventName: "UM_Event_ModularClick",
            UM_Key_ButtonName: "mail_passwordsignin",
            UM_Key_PageName: pageId,
            UM_Key_UserType: "Guest",
            UM_Key_SourcePage: router.route,
            UM_Key_ResourceID: code,
          });
          handleLogin();
        }}
        className={`${
          disabledLogin ? "bg-[#1673FF] opacity-[0.3]" : "bg-[#1673FF]"
        }  flex justify-center text-[#fff]  h-[48px] items-center text-[16px] font-['Mulish'] font-[700] rounded-[4px]`}
      >
        {t("login.goLogIn")}
      </div>

      <div className="flex justify-center text-[#333] text-[14px] font-['Mulish']  font-[400] mt-[16px] ">
        {t("login.newToSeekee")}
        <div
          className="text-[#1677ff] ms-[6px] font-[700] cursor-pointer"
          onClick={() => {
            sendSlsEvent({
              UM_Key_EventName: "UM_Event_ModularClick",
              UM_Key_ButtonName: "mailsignup",
              UM_Key_PageName: pageId,
              UM_Key_UserType: "Guest",
              UM_Key_SourcePage: router.route,
              UM_Key_ResourceID: code,
            });
            console.log("goRegistry-----");
            handleEmailChange("");
            goRegistry();
          }}
        >
          {t("login.signUp")}
        </div>
      </div>
      <UserTip></UserTip>

      <Overlay
        visible={showUnRegisterDialog}
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="flex flex-col items-center w-[calc(100vw)]  bg-[transparent]">
          <div className="w-[calc(100vw-40px)] relative bg-[#fff] rounded-[16px] p-[20px]">
            <div className="text-[18px] font-['Mulish'] font-[700] text-[#333] mb-[20px]">
              {t("login.accountNotRegistered")}
            </div>
            <div className="text-[14px] font-[700] font-['Mulish'] text-[#999]">
              <span className="text-[#1677ff]">{formData.email}</span>
              <span className="text-[14px] font-[400] font-['Mulish'] text-[#999]  mb-[20px]">
                {t("login.accountNotRegisteredTip")}
              </span>
            </div>
            <div className="text-[14px] font-[400] font-['Mulish'] text-[#999]  mb-[20px]">
              {t("login.accountNotRegisteredTip2")}
            </div>
            <div
              onClick={handleGoRegistry}
              className=" h-[37px] bg-[#1677ff] font-['Mulish'] text-[#fff] font-[700] mb-[10px] cursor-pointer  rounded-[8px] flex justify-center items-center"
            >
              {t("login.goRegistry")}
            </div>
            <div
              onClick={handleUnregisterDialogClose}
              className=" h-[37px] bg-[#f5f5f5] text-[#333] font-['Mulish'] font-[700] cursor-pointer  rounded-[8px] flex justify-center items-center"
            >
              {t("login.cancel")}
            </div>
          </div>
        </div>
      </Overlay>
    </div>
  );
};

export default EmailForm;
